import styled from 'styled-components';

export const HeaderContainer = styled.div`
  display: block;
  box-shadow: ${({ theme }) => theme.appearance.box_shadow};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white_1};
  padding: 24px;
  border-radius: ${({ theme }) => theme.border_radius.lg}
    ${({ theme }) => theme.border_radius.lg} 0 0;
`;

export const StepsProgressLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}px;
  width: 100%;
`;

export const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
`;

export const StepCircle = styled.div<{
  $completed?: boolean;
  $current?: boolean;
}>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: ${({ $current }) => ($current ? '2px' : '1px')} solid
    ${({ theme, $completed }) =>
      $completed ? theme.colors.green_high_contrast : theme.colors.grey_3};
  color: ${({ theme, $completed, $current }) =>
    $completed
      ? theme.colors.grey_2
      : $current
        ? theme.colors.grey_2
        : theme.colors.grey_3};
  background: ${({ theme, $completed }) =>
    $completed ? theme.colors.green_4 : theme.colors.white_1};
  position: relative;
  z-index: 1;
  font-weight: ${({ theme, $current }) =>
    $current ? theme.fontWeights.bold : theme.fontWeights.light};
`;

export const WarningCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -10px;
  right: -10px;
  border: 1px solid;
  background: ${({ theme }) => theme.colors.red_5};
  z-index: 1;
`;

export const Connector = styled.div`
  position: absolute;
  top: 50%;
  left: 14px;
  right: 14px;
  height: 1px;
  background: ${({ theme }) => theme.colors.grey_4};
  transform: translateY(-50%);
  z-index: 0;
`;

export const ResponsiveHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.xl}px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg}px;
  }
`;

export const ResponsiveContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.md}px;
  align-items: stretch;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const ContentDetails = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: fit-content;
  width: auto;
  text-align: left;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    width: 100%;
    text-align: center;
    align-items: center;

    & > div {
      justify-content: center !important;
    }
  }
`;

export const ResponsiveLinkWrapper = styled.div`
  width: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    width: 100%;
  }
`;

export const ResponsiveActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.sm + theme.spacing.xs}px;
  flex: 1 1 52%;
  max-width: 58%;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    width: 100%;
    max-width: none;
    align-items: center;
  }
`;

export const LinkContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    justify-content: center;
  }
`;

export const MainContentHeaderActions = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const ManagedProgressSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}px;
  padding-top: ${({ theme }) => theme.spacing.lg}px;
  border-top: thin solid ${({ theme }) => theme.colors.grey_4};
`;

export const ManagedProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}px;
`;

export const TeamSummary = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-end;
  gap: ${({ theme }) => theme.spacing.sm}px;
  width: fit-content;
  max-width: 100%;
  margin-top: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
    align-self: auto;
    width: 100%;
    margin-top: 0;
  }
`;

export const TeamSummaryList = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const TeamSummaryMember = styled.article`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}px;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  padding: 0 ${({ theme }) => theme.spacing.md}px;
  border-left: thin solid ${({ theme }) => theme.colors.grey_4};

  &:first-child {
    padding-left: 0;
    border-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    width: 100%;
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing.sm}px 0;
    border-left: 0;
    border-top: thin solid ${({ theme }) => theme.colors.grey_4};

    &:first-child {
      padding-top: 0;
      border-top: 0;
    }
  }
`;

export const TeamSummaryIdentity = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;

  & > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const TeamSummaryActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.xs}px;
  flex: 0 0 auto;
`;

export const TeamSummaryContactLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs}px;
  width: ${({ theme }) => theme.spacing.lg}px;
  height: ${({ theme }) => theme.spacing.lg}px;
  border-radius: ${({ theme }) => theme.border_radius.full};
  color: ${({ theme }) => theme.colors.purple_2};
  background: ${({ theme }) => theme.colors.purple_4};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.white_1};
    background: ${({ theme }) => theme.colors.purple_2};
  }
`;
